import { MenuProps, Tooltip } from 'antd';
import { Button, Dropdown } from 'antd';
import {
  AudioOutlined,
  CustomerServiceOutlined,
  FileImageOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import {
  GLOBAL_SCENE_INDEX,
  MotionType,
  Scene,
  SceneItemType,
  TransitionEffect,
} from '@monorepo/types';
import { FunctionComponent } from 'react';
import { useVideoContext } from '../../../../hooks/use-video';
import { useTranslation } from 'react-i18next';

interface TypeConfig {
  label: string;
  icon: React.ReactNode;
}

interface Props {
  scene: Scene;
}

const AddSceneItemButton: FunctionComponent<Props> = ({ scene }) => {
  const { t } = useTranslation();
  const { createSceneItem, video, setLatestSceneItem } = useVideoContext();

  const typeConfig: Record<SceneItemType, TypeConfig> = {
    [SceneItemType.Voiceover]: {
      label: t('videoEditor.scene.itemTypes.voiceover'),
      icon: <AudioOutlined />,
    },
    [SceneItemType.Audio]: {
      label: t('videoEditor.scene.itemTypes.audio'),
      icon: <CustomerServiceOutlined />,
    },
    [SceneItemType.Asset]: {
      label: t('videoEditor.scene.itemTypes.asset'),
      icon: <FileImageOutlined />,
    },
  };

  const items: MenuProps['items'] = Object.values(SceneItemType)
    .filter((type) => {
      const voiceoverExists = scene.items.find(
        (sceneItem) => sceneItem.type === SceneItemType.Voiceover
      );

      if (
        type === SceneItemType.Voiceover &&
        (voiceoverExists || scene.index === GLOBAL_SCENE_INDEX)
      ) {
        return false;
      }

      return true;
    })
    .map((type) => ({
      key: type,
      label: typeConfig[type].label,
      icon: typeConfig[type].icon,
    }));

  const getDataByType = (type: SceneItemType) => {
    if (type === SceneItemType.Asset) {
      return {
        assetId: '',
        transition: TransitionEffect.Fade,
        motion: MotionType.GENTLE_RANDOM_MOTION,
      };
    }
    if (type === SceneItemType.Voiceover) {
      return {
        voiceoverId: video?.voiceoverId || '',
        words: [],
      };
    }
    if (type === SceneItemType.Audio) {
      return {
        url: '',
        loop: true,
        volume: 1,
        playbackRate: 1,
      };
    }

    return {};
  };

  const handleMenuClick: MenuProps['onClick'] = async ({ key }) => {
    try {
      const newIndex = scene.items.length;

      await createSceneItem({
        sceneId: scene._id,
        videoId: scene.videoId,
        createDto: {
          start: 0,
          end: scene.duration,
          type: key as SceneItemType,
          index: newIndex,
          data: getDataByType(key as SceneItemType),
        },
      });

      setLatestSceneItem({ sceneId: scene._id, sceneItemIndex: newIndex });
    } catch (e) {
      console.error(`failed creating scene item: ${key}`, e);
    }
    // Handle selection based on type
  };

  return (
    <Dropdown
      menu={{
        items,
        onClick: handleMenuClick,
      }}
      placement="bottom"
      trigger={['click']}
    >
      <Tooltip
        placement={'left'}
        title={t('videoEditor.scene.addSceneItemTooltip')}
      >
        <Button type="text" size="middle" icon={<PlusCircleOutlined />} />
      </Tooltip>
    </Dropdown>
  );
};

export default AddSceneItemButton;
